import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Building2, FileText, GitBranch, User,
  LayoutDashboard, Crosshair, Gem, Store,
  Users, Sparkles, Network, Settings,
} from "lucide-react";
import { PremiumInput } from "./brand/PremiumInput";
import { useChainStore, cfBus } from "../data/chainStore";
import { initialsOf } from "./contacts/AddContactModal";

/* ═══════════════════════════════════════════════════════════════════
   CommandPalette — the canonical operational spotlight.
   ───────────────────────────────────────────────────────────────────
   Single global ⌘K listener. Single active surface. Two display modes:
     · Empty query  → grouped workspace / operations / account nav.
     · Typed query  → live results (contacts · properties · deals ·
                      documents) plus matching nav items.
   Also responds to the legacy `cf:open-search` cfBus event so the
   topbar `SearchButton` continues to work without a second modal. */

const NAV_ITEMS = [
  { to: "/today",              label: "Today",               group: "Workspace",  icon: LayoutDashboard, hint: "Daily operational cockpit" },
  { to: "/command",            label: "Flow Centre",      group: "Workspace",  icon: Crosshair,       hint: "Your daily queue" },
  { to: "/deal-rooms",         label: "Deal Rooms",          group: "Workspace",  icon: Building2,       hint: "Live transactions" },
  { to: "/offer-intelligence", label: "Offer Intelligence",  group: "Operations", icon: Gem,             hint: "AI offer evaluation" },
  { to: "/marketplace",        label: "Service Marketplace", group: "Operations", icon: Store,           hint: "Partners & referrals" },
  { to: "/documents",          label: "Document Hub",        group: "Operations", icon: FileText,        hint: "Vault & compliance" },
  { to: "/ai-insights",        label: "AI Risk Centre",      group: "Operations", icon: Sparkles,        hint: "Predictive intelligence" },
  { to: "/seller-portal",      label: "Seller Portal",       group: "Account",    icon: Users,           hint: "Seller view" },
  { to: "/buyer-portal",       label: "Buyer Portal",        group: "Account",    icon: Users,           hint: "Buyer view" },
  { to: "/branch-analytics",   label: "Branch Analytics",    group: "Account",    icon: Network,         hint: "Office benchmarking" },
  { to: "/settings",           label: "Settings",            group: "Account",    icon: Settings,        hint: "Workspace config" },
];

const TYPE_META = {
  contact:  { icon: User,      label: "Contact",  tone: "bg-blue-50 text-blue-700 border-blue-200" },
  property: { icon: Building2, label: "Property", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  deal:     { icon: GitBranch, label: "Deal",     tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  document: { icon: FileText,  label: "Document", tone: "bg-amber-50 text-amber-700 border-amber-200" },
};

export const CommandPalette = () => {
  const { contacts, properties, deals, findContact, findProperty } = useChainStore();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  // Open via ⌘K + the legacy `cf:open-search` event from the topbar
  // SearchButton. Close via Escape. One listener pair only.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setQ("");
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => cfBus.on("cf:open-search", () => { setQ(""); setOpen(true); }), []);

  const trimmed = q.trim();

  const searchResults = useMemo(() => {
    if (!trimmed) return [];
    const t = trimmed.toLowerCase();
    const items = [];
    contacts.forEach((c) => {
      if ((c.name || "").toLowerCase().includes(t) || (c.email || "").toLowerCase().includes(t) || (c.company || "").toLowerCase().includes(t)) {
        items.push({ kind: "contact", id: c.id, title: c.name, sub: `${c.role || ""}${c.company ? ` · ${c.company}` : ""}`, avatar: initialsOf(c.name), data: c });
      }
    });
    properties.forEach((p) => {
      if ((p.address || "").toLowerCase().includes(t) || (p.postcode || "").toLowerCase().includes(t)) {
        items.push({ kind: "property", id: p.id, title: (p.address || "").split(",")[0], sub: `${p.postcode || ""} · ${p.price || ""}`, thumb: p.thumb, data: p });
      }
    });
    deals.forEach((d) => {
      const p = findProperty(d.propertyId);
      const buyer = findContact(d.buyerId);
      const hay = `${p?.address || ""} ${buyer?.name || ""} ${d.id}`.toLowerCase();
      if (hay.includes(t)) {
        items.push({ kind: "deal", id: d.id, title: p?.address?.split(",")[0] || d.id, sub: `${d.stage || ""} · ${d.agreedPrice || ""} · ${buyer?.name || ""}`, thumb: p?.thumb, data: d });
      }
    });
    deals.forEach((d) => {
      d.documents?.forEach((doc) => {
        if ((doc.name || "").toLowerCase().includes(t) || (doc.tag || "").toLowerCase().includes(t)) {
          const p = findProperty(d.propertyId);
          items.push({ kind: "document", id: `${d.id}-${doc.id}`, title: doc.name, sub: `${doc.tag || ""} · ${p?.address?.split(",")[0] || ""}`, dealId: d.id });
        }
      });
    });
    return items.slice(0, 30);
  }, [trimmed, contacts, properties, deals, findContact, findProperty]);

  const navResults = useMemo(() => {
    if (!trimmed) return NAV_ITEMS;
    const t = trimmed.toLowerCase();
    return NAV_ITEMS.filter((i) => i.label.toLowerCase().includes(t) || i.hint.toLowerCase().includes(t));
  }, [trimmed]);

  const groupedNav = useMemo(() => navResults.reduce((acc, i) => {
    (acc[i.group] = acc[i.group] || []).push(i);
    return acc;
  }, {}), [navResults]);

  const handleSelectResult = (r) => {
    setOpen(false);
    if (r.kind === "deal") nav(`/deal-rooms/${r.id}`);
    else if (r.kind === "property") nav("/properties");
    /* P3 · Global Search · Open Contact on Selection (2026-02-28).
     *
     * A command palette should feel like teleportation, not a
     * search index — selecting a contact result must land the
     * operator directly on that contact's profile in a single
     * action.  The canonical route is `/contacts/:contactId`
     * (see App.js → <Route path="/contacts/:contactId" …>) so
     * navigating there closes the palette, preserves browser
     * history, and matches every other command-palette result
     * type (deal / document already resolve to the specific
     * record; contact was the last outlier). */
    else if (r.kind === "contact") nav(`/contacts/${r.id}`);
    else if (r.kind === "document") nav(`/deal-rooms/${r.dealId}`);
  };

  const handleSelectNav = (to) => { setOpen(false); nav(to); };

  if (!open) return null;

  const showEmpty = trimmed && searchResults.length === 0 && navResults.length === 0;

  return (
    <div
      data-testid="command-palette"
      className="fixed inset-0 z-[80] flex items-start justify-center px-4 pt-[14vh]"
      onClick={() => setOpen(false)}
    >
      <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="relative w-full max-w-[640px] rounded-2xl bg-white border border-slate-200 cf-shadow-xl overflow-hidden cf-fade-up"
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 text-[14px]">
          <PremiumInput
            variant="bare"
            format="search"
            clearable
            autoFocus
            value={q}
            onChange={setQ}
            placeholder="Search contacts, properties, deals · or jump to a workspace…"
            testid="command-palette-input"
            className="flex-1"
          />
          <span className="text-[10.5px] font-mono-cf text-slate-500 px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">esc</span>
        </div>

        <div className="max-h-[60vh] overflow-y-auto cf-scroll p-2">
          {/* Search results — only render when there is a query */}
          {trimmed && searchResults.length > 0 && (
            <div className="mb-2" data-testid="search-results">
              <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
                Results
              </p>
              <ul>
                {searchResults.map((r) => <SearchRow key={`${r.kind}-${r.id}`} r={r} onSelect={handleSelectResult} />)}
              </ul>
            </div>
          )}

          {/* Navigation items — always rendered; filtered when query is set */}
          {navResults.length > 0 && Object.entries(groupedNav).map(([group, list]) => (
            <div key={group} className="mb-2">
              <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">{group}</p>
              {list.map((i) => {
                const Icon = i.icon;
                return (
                  <button
                    key={i.to}
                    data-testid={`palette-${i.to}`}
                    onClick={() => handleSelectNav(i.to)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors group text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center group-hover:bg-indigo-50 group-hover:text-indigo-700 transition-colors">
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-slate-900 truncate">{i.label}</p>
                      <p className="text-[11px] text-slate-500 truncate">{i.hint}</p>
                    </div>
                    <ArrowRight size={13} className="text-slate-300 group-hover:text-indigo-600 transition-colors" />
                  </button>
                );
              })}
            </div>
          ))}

          {showEmpty && (
            <p className="text-center text-[12.5px] text-slate-500 py-8">No matches for "{trimmed}".</p>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-[10.5px] text-slate-500 bg-slate-50/60">
          <span>Press <span className="font-mono-cf text-slate-900">↵</span> to open</span>
          <span><span className="font-mono-cf text-slate-900">⌘K</span> to toggle anywhere</span>
        </div>
      </div>
    </div>
  );
};

const SearchRow = ({ r, onSelect }) => {
  const meta = TYPE_META[r.kind];
  const Icon = meta.icon;
  return (
    <li>
      <button
        onClick={() => onSelect(r)}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-indigo-50 transition-colors text-left"
        data-testid={`search-result-${r.kind}-${r.id}`}
      >
        {r.thumb ? (
          <div className="w-9 h-9 rounded-lg overflow-hidden bg-slate-100 shrink-0">
            <img src={r.thumb} alt="" className="w-full h-full object-cover" />
          </div>
        ) : r.avatar ? (
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 text-white text-[10.5px] font-bold flex items-center justify-center shrink-0">
            {r.avatar}
          </div>
        ) : (
          <div className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 ${meta.tone}`}>
            <Icon size={14} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[12.5px] font-bold text-slate-900 truncate">{r.title}</p>
          <p className="text-[10.5px] text-slate-500 truncate">{r.sub}</p>
        </div>
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-[0.14em] border ${meta.tone}`}>
          {meta.label}
        </span>
      </button>
    </li>
  );
};

export default CommandPalette;
